import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  where,
  serverTimestamp,
  onSnapshot,
  type Unsubscribe,
} from '@/lib/firestore'
import { db } from '@/config/firebase'
import { useAuthStore } from '@/stores/auth'
import { getTenantConstraint, getTenantData } from '@/utils/tenantQuery'
import { useActivityLog } from '@/composables/useActivityLog'
import { useNotificationsStore } from '@/stores/notifications'
import { useSalesOrdersStore } from '@/views/SalesOrders/store'
import type { Quotation, QuotationStatus, ProjectType } from '@/types'
import type { QuotationsState } from './state'

export interface CreateQuotationData {
  organizationId?: string
  prospectId?: string
  projectName: string
  projectType: ProjectType
  description: string
  hasDesigns?: boolean
  desiredDeadline?: Date
  estimatedBudgetRange?: string
  additionalNotes?: string
}

export interface UpdateQuotationData {
  projectName?: string
  projectType?: ProjectType
  description?: string
  hasDesigns?: boolean
  desiredDeadline?: Date
  estimatedBudgetRange?: string
  additionalNotes?: string
  status?: QuotationStatus
  quotedAmount?: number
  validUntil?: Date
}

export const createActions = (state: QuotationsState) => {
  const { logCreate, logUpdate, logDelete, detectChanges } = useActivityLog()
  const notificationsStore = useNotificationsStore()

  let globalUnsubscribe: Unsubscribe | null = null

  const mapQuotationDoc = (docSnap: { id: string; data: () => Record<string, unknown> }): Quotation => {
    const data = docSnap.data()
    return {
      id: docSnap.id,
      tenantId: data.tenantId,
      organizationId: data.organizationId,
      prospectId: data.prospectId,
      projectName: data.projectName,
      projectType: data.projectType,
      description: data.description,
      hasDesigns: data.hasDesigns || false,
      desiredDeadline: (data.desiredDeadline as any)?.toDate?.(),
      estimatedBudgetRange: data.estimatedBudgetRange,
      additionalNotes: data.additionalNotes,
      attachments: data.attachments || [],
      status: data.status,
      quotedAmount: data.quotedAmount,
      validUntil: (data.validUntil as any)?.toDate?.(),
      convertedToProjectId: data.convertedToProjectId,
      convertedToSalesOrderId: data.convertedToSalesOrderId,
      createdBy: data.createdBy || '',
      createdAt: (data.createdAt as any)?.toDate?.() || new Date(),
      updatedAt: (data.updatedAt as any)?.toDate?.() || new Date(),
      updatedBy: data.updatedBy || '',
    } as Quotation
  }

  const subscribeGlobal = (): void => {
    if (globalUnsubscribe) return
    state.isLoading = true
    const authStore = useAuthStore()
    const tenantConstraint = getTenantConstraint()
    const baseConstraints = tenantConstraint ? [tenantConstraint] : []

    let q = query(collection(db, 'quotations'), ...baseConstraints, orderBy('createdAt', 'desc'))
    if (authStore.user?.role === 'client' || authStore.user?.role === 'member') {
      if (authStore.user.organizationId) {
        q = query(collection(db, 'quotations'), ...baseConstraints, where('organizationId', '==', authStore.user.organizationId), orderBy('createdAt', 'desc'))
      }
    }

    globalUnsubscribe = onSnapshot(q, (snapshot) => {
      state.quotations = snapshot.docs.map(mapQuotationDoc)
      state.isLoading = false
    }, (error) => {
      console.error('Quotations subscription error:', error)
      state.isLoading = false
    })
  }

  const unsubscribeGlobal = (): void => {
    if (globalUnsubscribe) {
      globalUnsubscribe()
      globalUnsubscribe = null
    }
  }

  const fetchQuotations = async (): Promise<void> => {
    state.isLoading = true
    state.error = null

    try {
      const authStore = useAuthStore()
      const tenantConstraint = getTenantConstraint()
      const baseConstraints = tenantConstraint ? [tenantConstraint] : []

      let q = query(collection(db, 'quotations'), ...baseConstraints, orderBy('createdAt', 'desc'))

      // Filter by organization for clients
      if (authStore.user?.role === 'client' || authStore.user?.role === 'member') {
        if (authStore.user.organizationId) {
          q = query(
            collection(db, 'quotations'),
            ...baseConstraints,
            where('organizationId', '==', authStore.user.organizationId),
            orderBy('createdAt', 'desc')
          )
        }
      }

      const snapshot = await getDocs(q)

      state.quotations = snapshot.docs.map((docSnap) => {
        const data = docSnap.data()
        return {
          id: docSnap.id,
          tenantId: data.tenantId,
          organizationId: data.organizationId,
          prospectId: data.prospectId,
          projectName: data.projectName,
          projectType: data.projectType,
          description: data.description,
          hasDesigns: data.hasDesigns || false,
          desiredDeadline: data.desiredDeadline?.toDate(),
          estimatedBudgetRange: data.estimatedBudgetRange,
          additionalNotes: data.additionalNotes,
          attachments: data.attachments || [],
          status: data.status,
          quotedAmount: data.quotedAmount,
          validUntil: data.validUntil?.toDate(),
          convertedToProjectId: data.convertedToProjectId,
          convertedToSalesOrderId: data.convertedToSalesOrderId,
          createdBy: data.createdBy || '',
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          updatedBy: data.updatedBy || '',
        } as Quotation
      })
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Error fetching quotations'
      console.error('Error fetching quotations:', error)
    } finally {
      state.isLoading = false
    }
  }

  const fetchQuotationById = async (id: string): Promise<Quotation | null> => {
    try {
      const docRef = doc(db, 'quotations', id)
      const docSnap = await getDoc(docRef)

      if (!docSnap.exists()) {
        return null
      }

      const data = docSnap.data()
      return {
        id: docSnap.id,
        tenantId: data.tenantId,
        organizationId: data.organizationId,
        prospectId: data.prospectId,
        projectName: data.projectName,
        projectType: data.projectType,
        description: data.description,
        hasDesigns: data.hasDesigns || false,
        desiredDeadline: data.desiredDeadline?.toDate(),
        estimatedBudgetRange: data.estimatedBudgetRange,
        additionalNotes: data.additionalNotes,
        attachments: data.attachments || [],
        status: data.status,
        quotedAmount: data.quotedAmount,
        validUntil: data.validUntil?.toDate(),
        convertedToProjectId: data.convertedToProjectId,
        convertedToSalesOrderId: data.convertedToSalesOrderId,
        createdBy: data.createdBy || '',
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
        updatedBy: data.updatedBy || '',
      } as Quotation
    } catch (error) {
      console.error('Error fetching quotation:', error)
      return null
    }
  }

  const createQuotation = async (data: CreateQuotationData): Promise<string> => {
    const authStore = useAuthStore()
    const userId = authStore.user?.id || ''
    // Use provided organizationId or fall back to user's organizationId
    const organizationId = data.organizationId || authStore.user?.organizationId || ''

    const quotationData = {
      ...getTenantData(),
      ...data,
      organizationId,
      status: 'requested' as QuotationStatus,
      desiredDeadline: data.desiredDeadline || null,
      hasDesigns: data.hasDesigns || false,
      createdBy: userId,
      updatedBy: userId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }

    const docRef = await addDoc(collection(db, 'quotations'), quotationData)

    const newQuotation: Quotation = {
      id: docRef.id,
      organizationId,
      prospectId: data.prospectId,
      projectName: data.projectName,
      projectType: data.projectType,
      description: data.description,
      hasDesigns: data.hasDesigns || false,
      desiredDeadline: data.desiredDeadline,
      estimatedBudgetRange: data.estimatedBudgetRange,
      additionalNotes: data.additionalNotes,
      status: 'requested',
      createdBy: userId,
      updatedBy: userId,
      createdAt: new Date(),
      updatedAt: new Date(),
    }

    state.quotations.unshift(newQuotation)

    await logCreate('quotation', docRef.id, data.projectName)

    // Notify admins and operators about new quotation request
    await notificationsStore.notifyAdminsAndOperators(
      'quotation_created',
      'Nueva cotización solicitada',
      `Se ha solicitado una cotización para "${data.projectName}"`,
      'quotation',
      docRef.id
    )

    return docRef.id
  }

  const updateQuotation = async (id: string, data: UpdateQuotationData): Promise<void> => {
    const authStore = useAuthStore()
    const userId = authStore.user?.id || ''
    const docRef = doc(db, 'quotations', id)
    const existingQuotation = state.quotations.find((q) => q.id === id)

    const updateData = {
      ...data,
      desiredDeadline: data.desiredDeadline || null,
      validUntil: data.validUntil || null,
      updatedBy: userId,
      updatedAt: serverTimestamp(),
    }

    await updateDoc(docRef, updateData)

    const index = state.quotations.findIndex((q) => q.id === id)
    if (index !== -1) {
      state.quotations[index] = {
        ...state.quotations[index],
        ...data,
        updatedBy: userId,
        updatedAt: new Date(),
      }
    }

    if (existingQuotation) {
      const fieldsToTrack: (keyof Quotation)[] = ['status', 'projectName', 'quotedAmount']
      const changes = detectChanges(
        existingQuotation as unknown as Record<string, unknown>,
        data as Record<string, unknown>,
        fieldsToTrack as string[]
      )
      if (changes.length > 0) {
        await logUpdate('quotation', id, changes, existingQuotation.projectName)
      }
    }
  }

  const updateQuotationStatus = async (id: string, status: QuotationStatus): Promise<void> => {
    const authStore = useAuthStore()
    const userId = authStore.user?.id || ''
    const docRef = doc(db, 'quotations', id)
    const existingQuotation = state.quotations.find((q) => q.id === id)

    await updateDoc(docRef, {
      status,
      updatedBy: userId,
      updatedAt: serverTimestamp(),
    })

    const index = state.quotations.findIndex((q) => q.id === id)
    if (index !== -1) {
      state.quotations[index] = {
        ...state.quotations[index],
        status,
        updatedBy: userId,
        updatedAt: new Date(),
      }
    }

    if (existingQuotation) {
      await logUpdate('quotation', id, [
        { field: 'status', oldValue: existingQuotation.status, newValue: status },
      ], existingQuotation.projectName)

      // Notify about quotation status change
      await notificationsStore.notifyAdminsAndOperators(
        'quotation_responded',
        'Cotización actualizada',
        `"${existingQuotation.projectName}" cambió a ${status}`,
        'quotation',
        id
      )
    }
  }

  const submitQuote = async (id: string, quotedAmount: number, validUntil: Date): Promise<void> => {
    const authStore = useAuthStore()
    const userId = authStore.user?.id || ''
    const docRef = doc(db, 'quotations', id)

    await updateDoc(docRef, {
      status: 'sent' as QuotationStatus,
      quotedAmount,
      validUntil,
      updatedBy: userId,
      updatedAt: serverTimestamp(),
    })

    const index = state.quotations.findIndex((q) => q.id === id)
    if (index !== -1) {
      state.quotations[index] = {
        ...state.quotations[index],
        status: 'sent',
        quotedAmount,
        validUntil,
        updatedBy: userId,
        updatedAt: new Date(),
      }
    }

    await logUpdate('quotation', id, [
      { field: 'status', oldValue: 'requested', newValue: 'sent' },
      { field: 'quotedAmount', oldValue: undefined, newValue: quotedAmount },
    ])
  }

  const deleteQuotation = async (id: string): Promise<void> => {
    const quotation = state.quotations.find((q) => q.id === id)

    await deleteDoc(doc(db, 'quotations', id))

    state.quotations = state.quotations.filter((q) => q.id !== id)

    if (quotation) {
      await logDelete('quotation', id, quotation.projectName)
    }
  }

  const setFilters = (filters: Partial<QuotationsState['filters']>): void => {
    state.filters = { ...state.filters, ...filters }
  }

  const getQuotationById = (id: string): Quotation | undefined => {
    return state.quotations.find((q) => q.id === id)
  }

  const convertToSalesOrder = async (quotationId: string): Promise<string> => {
    const authStore = useAuthStore()
    const userId = authStore.user?.id || ''
    const salesOrdersStore = useSalesOrdersStore()

    // Get the quotation
    const quotation = state.quotations.find((q) => q.id === quotationId)
    if (!quotation) {
      throw new Error('Quotation not found')
    }

    if (quotation.status !== 'accepted') {
      throw new Error('Only accepted quotations can be converted to sales orders')
    }

    if (quotation.convertedToSalesOrderId) {
      throw new Error('Quotation already converted to sales order')
    }

    if (!quotation.organizationId) {
      throw new Error('Quotation must have an organization')
    }

    if (!quotation.quotedAmount) {
      throw new Error('Quotation must have a quoted amount')
    }

    // Create the sales order
    const salesOrderId = await salesOrdersStore.createSalesOrder({
      quotationId,
      organizationId: quotation.organizationId,
      items: [
        {
          description: quotation.projectName,
          quantity: 1,
          unitPrice: quotation.quotedAmount,
        },
      ],
      taxRate: 0,
      discount: 0,
      notes: `Generado desde cotización: ${quotation.projectName}`,
      validUntil: quotation.validUntil,
    })

    // Update the quotation with the sales order ID
    const docRef = doc(db, 'quotations', quotationId)
    await updateDoc(docRef, {
      convertedToSalesOrderId: salesOrderId,
      updatedBy: userId,
      updatedAt: serverTimestamp(),
    })

    // Update local state
    const index = state.quotations.findIndex((q) => q.id === quotationId)
    if (index !== -1) {
      state.quotations[index] = {
        ...state.quotations[index],
        convertedToSalesOrderId: salesOrderId,
        updatedBy: userId,
        updatedAt: new Date(),
      }
    }

    return salesOrderId
  }

  return {
    subscribeGlobal,
    unsubscribeGlobal,
    fetchQuotations,
    fetchQuotationById,
    createQuotation,
    updateQuotation,
    updateQuotationStatus,
    submitQuote,
    deleteQuotation,
    setFilters,
    getQuotationById,
    convertToSalesOrder,
  }
}
